import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { LoadingPage } from "@/components/ui/loading-spinner";
import { 
  ArrowLeft, DollarSign, TrendingUp, GraduationCap, Lightbulb, 
  Clock, BarChart3, Briefcase, CheckCircle2, Heart, Award,
  Building2, MapPin, Calendar, Target
} from "lucide-react";

const CareerDetail = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCareer();
  }, [id]);

  const fetchCareer = async () => {
    try {
      const response = await axios.get(`${API}/careers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCareer(response.data);
    } catch (error) {
      toast.error("Failed to load career details");
      navigate("/careers");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Technology: "bg-primary/10 text-primary border-primary/20",
      Healthcare: "bg-success/10 text-success border-success/20",
      Creative: "bg-purple-100 text-purple-700 border-purple-200",
      Business: "bg-warning/10 text-warning border-warning/20",
      Education: "bg-pink-100 text-pink-700 border-pink-200",
      Engineering: "bg-blue-100 text-blue-700 border-blue-200",
      Legal: "bg-indigo-100 text-indigo-700 border-indigo-200",
      Government: "bg-teal-100 text-teal-700 border-teal-200",
      Hospitality: "bg-orange-100 text-orange-700 border-orange-200",
    };
    return colors[category] || "bg-muted text-muted-foreground border-border";
  };

  const getWorkLifeColor = (balance) => {
    const colors = {
      'Very Good': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'Good': 'bg-green-100 text-green-700 border-green-200',
      'Moderate': 'bg-amber-100 text-amber-700 border-amber-200',
      'Challenging': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[balance] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getGrowthColor = (potential) => {
    const colors = {
      'Very High': 'bg-purple-100 text-purple-700 border-purple-200',
      'High': 'bg-blue-100 text-blue-700 border-blue-200',
      'Moderate': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Limited': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[potential] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading career details..." />
      </div>
    );
  }

  if (!career) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-testid="career-detail-container">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6 hover:bg-primary/10" 
          onClick={() => navigate("/careers")} 
          data-testid="back-to-careers-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Careers
        </Button>

        {/* Hero Card */}
        <Card className="mb-8 border-none shadow-lg overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
          <CardHeader className="pb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <Badge variant="outline" className={`${getCategoryColor(career.category)} mb-4 text-sm`}>
                  {career.category}
                </Badge>
                <CardTitle className="text-3xl md:text-4xl mb-4 leading-tight" data-testid="career-title">
                  {career.title}
                </CardTitle>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed" data-testid="career-description">
                  {career.description}
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Salary Cards - Dual Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {career.salary_range_india && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground mb-1">Salary Range (India)</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600" data-testid="career-salary-india">
                      {career.salary_range_india}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {career.salary_range_global && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground mb-1">Salary Range (Global)</p>
                    <p className="text-xl md:text-2xl font-bold text-blue-600" data-testid="career-salary-global">
                      {career.salary_range_global}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {career.job_outlook && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Job Outlook</p>
                    <p className="text-sm font-semibold text-foreground">{career.job_outlook}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {career.timeline_to_enter && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Timeline</p>
                    <p className="text-sm font-semibold text-foreground">{career.timeline_to_enter}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {career.work_life_balance && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Work-Life Balance</p>
                    <Badge variant="outline" className={`${getWorkLifeColor(career.work_life_balance)} text-xs mt-1`}>
                      {career.work_life_balance}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {career.growth_potential && (
            <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Growth Potential</p>
                    <Badge variant="outline" className={`${getGrowthColor(career.growth_potential)} text-xs mt-1`}>
                      {career.growth_potential}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Work Environment */}
        {career.work_environment && (
          <Card className="mb-8 border-none shadow-md">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cyan-600" />
                </div>
                Work Environment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 text-base px-4 py-2">
                {career.work_environment}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Required Education */}
          {career.required_education && career.required_education.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-primary" />
                  </div>
                  Required Education
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3" data-testid="career-education">
                  {career.required_education.map((edu, index) => (
                    <li key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-medium">{edu}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Required Skills */}
          {career.skills && career.skills.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-warning" />
                  </div>
                  Required Skills
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="career-skills">
                  {career.skills.map((skill, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="px-4 py-2 text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certifications */}
          {career.certifications && career.certifications.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-600" />
                  </div>
                  Recommended Certifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {career.certifications.map((cert, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="px-4 py-2 text-sm font-medium bg-amber-50 text-amber-700 border-amber-200"
                    >
                      {cert}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Companies */}
          {career.top_companies && career.top_companies.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  Top Companies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {career.top_companies.map((company, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 border-blue-200"
                    >
                      {company}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Day in Life */}
        {career.day_in_life && (
          <Card className="mb-8 border-none shadow-md">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                </div>
                A Day in the Life
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">{career.day_in_life}</p>
            </CardContent>
          </Card>
        )}

        {/* Career Roadmap */}
        {career.roadmap && career.roadmap.length > 0 && (
          <Card className="mb-8 border-none shadow-md">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-success" />
                </div>
                Career Roadmap
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {career.roadmap.map((phase, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 font-bold text-primary text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1 text-primary">{phase.phase}</h4>
                      <p className="text-sm text-muted-foreground">{phase.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compare Button */}
        <div className="flex justify-center mt-8">
          <Button 
            size="lg" 
            className="shadow-lg"
            onClick={() => navigate('/career-comparison')}
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Compare with Other Careers
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CareerDetail;
